import React, { useCallback, useReducer } from 'react';
import { createComboGroupReducer } from './createComboGroupReducer';
import {
	CreateComboGroupContext,
	createComboGroupInitialState,
	TCreateComboGroupVisibleForm,
	TTypeOfCreateComboGroupModalWindow,
} from './createComboGroupContext';
import { CreateComboGroupActionTypes } from './createComboGroupActions';
import { IItemForGetShortInfo, TLanguageIDs, IDefaultLanguage, ICreateComboGroup } from 'types';

const CreateComboGroupState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createComboGroupReducer, createComboGroupInitialState);

	const changeVisibleForm = useCallback((value: TCreateComboGroupVisibleForm) => {
		dispatch({
			type: CreateComboGroupActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateComboGroupActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateComboGroupActionTypes.SET_CUSTOMER,
			payload: {
				customer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateComboGroupActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: CreateComboGroupActionTypes.SET_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setFetchedDishes = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateComboGroupActionTypes.SET_FETCHED_DISHES,
			payload: {
				dishes: value,
			},
		});
	}, []);

	const setDishes = useCallback((value: string[]) => {
		dispatch({
			type: CreateComboGroupActionTypes.SET_DISHES,
			payload: {
				dishes: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: CreateComboGroupActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setFetchedComboItems = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateComboGroupActionTypes.SET_FETCHED_COMBO_ITEMS,
			payload: {
				comboItems: value,
			},
		});
	}, []);

	const setComboItems = useCallback((value: string[]) => {
		dispatch({
			type: CreateComboGroupActionTypes.SET_COMBO_ITEMS,
			payload: {
				comboItems: value,
			},
		});
	}, []);

	const updateComboItems = useCallback(
		(value: IItemForGetShortInfo[]) => {
			const IDsOfComboItemsShortInfo = value.map((el) => el.id);
			const updatedSelectedComboItems = state.selectedComboItems.filter((el) =>
				IDsOfComboItemsShortInfo.includes(el),
			);
			dispatch({
				type: CreateComboGroupActionTypes.SET_COMBO_ITEMS,
				payload: {
					comboItems: updatedSelectedComboItems,
				},
			});
		},
		[state.selectedComboItems],
	);

	const changeUpperLimit = useCallback((value: string) => {
		dispatch({
			type: CreateComboGroupActionTypes.CHANGE_UPPER_LIMIT,
			payload: {
				upperLimit: value,
			},
		});
	}, []);

	const changeLowerLimit = useCallback((value: string) => {
		dispatch({
			type: CreateComboGroupActionTypes.CHANGE_LOWER_LIMIT,
			payload: {
				lowerLimit: value,
			},
		});
	}, []);

	const setFetchedTranslations = useCallback(
		(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
			const translations = value.map((id) => ({ languageId: id, name: '' }));
			dispatch({
				type: CreateComboGroupActionTypes.SET_FETCHED_TRANSLATIONS,
				payload: {
					translations,
					defaultCustomerLanguage,
				},
			});
		},
		[],
	);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: CreateComboGroupActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateComboGroupActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: ICreateComboGroup | null) => {
		dispatch({
			type: CreateComboGroupActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfCreateComboGroupModalWindow) => {
		dispatch({
			type: CreateComboGroupActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateComboGroupActionTypes.RESET_FIELDS }), []);

	return (
		<CreateComboGroupContext.Provider
			value={{
				changeVisibleForm,
				setFetchedCustomers,
				setCustomer,
				setFetchedRestaurants,
				setRestaurants,
				setFetchedDishes,
				setDishes,
				changeName,
				setFetchedComboItems,
				setComboItems,
				updateComboItems,
				changeUpperLimit,
				changeLowerLimit,
				setFetchedTranslations,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				visibleForm: state.visibleForm,
				fetchedCustomers: state.fetchedCustomers,
				selectedCustomer: state.selectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				selectedRestaurants: state.selectedRestaurants,
				fetchedDishes: state.fetchedDishes,
				selectedDishes: state.selectedDishes,
				name: state.name,
				fetchedComboItems: state.fetchedComboItems,
				selectedComboItems: state.selectedComboItems,
				upperLimit: state.upperLimit,
				lowerLimit: state.lowerLimit,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CreateComboGroupContext.Provider>
	);
};

export default CreateComboGroupState;
